import { useEffect, useState } from "react";
import { Activity, BarChart3, Scale, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const CARDS_TEMPLATE = [
  {
    id: "project-watch",
    overline: "Section 01 · Operations",
    title: "Project Watch",
    body: "Real-time status of Canadian mining projects across exploration, permitting, construction, and production. Capex, IBA progress, and EA milestones — tracked weekly.",
    metricKey: "projects",
    metricLabel: "Projects Tracked",
    delta: "+3 this quarter",
    deltaPositive: true,
    icon: Activity,
    accent: "boreal",
  },
  {
    id: "commodity-signals",
    overline: "Section 02 · Markets",
    title: "Commodity Signals",
    body: "Daily commodity prices with Canadian-context analysis: who benefits, who hedges, and where capital is rotating. Gold, copper, nickel, lithium, uranium and more.",
    metricKey: "commodities",
    metricLabel: "Commodities Covered",
    delta: "Cu +1.21% today",
    deltaPositive: true,
    icon: BarChart3,
    accent: "copper",
  },
  {
    id: "policy-permitting",
    overline: "Section 03 · Policy",
    title: "Policy & Permitting",
    body: "Federal, provincial, territorial and First Nations actions affecting mining — from C-69 amendments and critical-minerals strategy to Yukon Peel Watershed orders.",
    metricKey: "policyItems",
    metricLabel: "Active Policy Files",
    delta: "4 federal bills in review",
    deltaPositive: false,
    icon: Scale,
    accent: "navy",
  },
];

const accentBar = {
  navy: "bg-bb-navy",
  copper: "bg-bb-copper",
  boreal: "bg-bb-boreal",
};

const accentText = {
  navy: "text-bb-navy",
  copper: "text-bb-copper",
  boreal: "text-bb-boreal",
};

export const SummaryCards = () => {
  const [counts, setCounts] = useState({
    projects: 15,
    commodities: 10,
    policyItems: 12,
  });

  useEffect(() => {
    let mounted = true;
    api
      .get("/stats")
      .then((r) => mounted && r.data && setCounts((s) => ({ ...s, ...r.data })))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const CARDS = CARDS_TEMPLATE.map((c) => ({
    ...c,
    metric: String(counts[c.metricKey] ?? "—"),
  }));

  return (
    <section
      className="py-14 lg:py-20 border-b border-border"
      data-testid="summary-cards-section"
    >
      <div className="bb-container">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <div className="bb-overline text-bb-copper">The Brief</div>
            <h2 className="font-archivo text-3xl lg:text-4xl text-bb-navy mt-2 font-bold tracking-tight">
              Three desks. One sector.
            </h2>
          </div>
          <p className="text-sm text-bb-slate max-w-md">
            BedrockBulletin organises its coverage around three editorial desks
            — operations, markets, and policy — each producing a daily brief.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.id}
                className="bg-white p-7 lg:p-8 flex flex-col gap-5 group hover:bg-bb-offwhite transition-colors"
                data-testid={`summary-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-1 h-12 ${accentBar[c.accent]}`} />
                  <Icon
                    size={28}
                    strokeWidth={1.6}
                    className={`${accentText[c.accent]}`}
                  />
                </div>
                <div className="bb-overline text-bb-slate">{c.overline}</div>
                <h3 className="font-archivo text-2xl lg:text-3xl text-bb-navy font-bold tracking-tight leading-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-bb-slate leading-relaxed">{c.body}</p>
                <div className="mt-3 pt-5 border-t border-border flex items-end justify-between">
                  <div>
                    <div className="font-mono text-3xl text-bb-navy font-semibold">
                      {c.metric}
                    </div>
                    <div className="bb-overline mt-1 text-bb-slate">
                      {c.metricLabel}
                    </div>
                  </div>
                  <span
                    className={`text-[11px] font-mono px-2 py-1 ${
                      c.deltaPositive ? "bb-tag-positive" : "bb-tag-negative"
                    }`}
                  >
                    {c.delta}
                  </span>
                </div>
                <a
                  href="#"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-archivo uppercase tracking-widest text-bb-navy bb-link self-start"
                  data-testid={`summary-card-${c.id}-link`}
                >
                  Explore the desk
                  <ArrowUpRight size={14} strokeWidth={2.4} />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SummaryCards;
